import axiosLib from 'axios'
import { AxiosResponse } from 'axios'
import { getKeyLang } from '../helpers'
import {
  IBookingRequest,
  IBookingResponse,
  IDate,
  IDateCheckout,
  IDatesCheckoutParamsQuery,
  IDatesParamsQuery,
  Identificator,
  IDestination,
  IHotel,
  IHotelDetailParamsQuery,
  IHotelsParamsQuery,
  IInitDataParametersQuery,
  IInitDataResponse,
  ILanguage,
  ILoyaltyProgram,
  IPrice,
  IVisitCategory,
  IVisitTypeCategoryParamsQuery,
} from '../store/data'
import axios from './axios'

const GET_ANOTHER_REQUEST_DEBOUNCE_INTERVAL = 10
const API_V2_PUBLIC_KEY_HEADER = 'X-SpaPortal-Public-Key'
const OBJECT_ID_PATTERN = /^[0-9a-fA-F]{24}$/

let calendarDatesDebounceTimer: NodeJS.Timeout | null = null
let calendarDatesRequestSequence = 0

type CalendarDatesBulkItem = IDatesParamsQuery & {
  key: string
}

type CalendarDatesBulkResponse = {
  results: Array<{
    data: IDate[]
    key: string | null
  }>
}

type PendingCalendarDatesRequest = {
  params: IDatesParamsQuery
  reject: (reason?: unknown) => void
  requestKey: string
  resolve: (value: AxiosResponse<IDate[]>) => void
}

let pendingCalendarDatesRequests: PendingCalendarDatesRequest[] = []

let hotelsAbortController: AbortController | null = null
let hotelsDebounceTimer: NodeJS.Timeout | null = null

let visitTypeCategoriesAbortController: AbortController | null = null
let visitTypeCategoriesDebounceTimer: NodeJS.Timeout | null = null

const getCalendarDatesBulkUrl = (): string => {
  const baseURL = axios.defaults.baseURL

  if (!baseURL) {
    return '/api/v2/calendar/dates/bulk'
  }

  if (/\/api\/v1\/?$/.test(baseURL)) {
    return `${baseURL.replace(/\/api\/v1\/?$/, '/api/v2')}/calendar/dates/bulk`
  }

  if (/\/api\/?$/.test(baseURL)) {
    return `${baseURL.replace(/\/api\/?$/, '/api/v2')}/calendar/dates/bulk`
  }

  if (baseURL.startsWith('/')) {
    return '/api/v2/calendar/dates/bulk'
  }

  return new URL('/api/v2/calendar/dates/bulk', baseURL).toString()
}

const buildCalendarDatesRequestKey = (params: IDatesParamsQuery): string =>
  JSON.stringify({
    currency: params.currency ?? null,
    date_from: params.date_from ?? null,
    date_to: params.date_to ?? null,
    destination_id: params.destination_id ?? null,
    destinationId: params.destinationId ?? null,
    hotel_id: params.hotel_id ?? null,
    hotelId: params.hotelId ?? null,
    room_type_id: params.room_type_id ?? null,
    roomTypeId: params.roomTypeId ?? null,
    visit_type_id: params.visit_type_id ?? null,
    visitTypeId: params.visitTypeId ?? null,
  })

const getObjectIdOrNull = (id?: Identificator | string | null): string | null => {
  if (typeof id !== 'string') {
    return null
  }

  return OBJECT_ID_PATTERN.test(id) ? id : null
}

const getCalendarDatesV2Id = (
  explicitId: string | null | undefined,
  legacyOrObjectId: Identificator | undefined
): string | null => explicitId ?? getObjectIdOrNull(legacyOrObjectId)

const getCalendarDatesBulkPublicKey = () =>
  process.env.NEXT_PUBLIC_SP3_API_TOKEN || process.env.NEXT_PUBLIC_API_TOKEN || ''

export const getDatesBulkApiRequest = (
  items: CalendarDatesBulkItem[]
): Promise<AxiosResponse<CalendarDatesBulkResponse>> =>
  axios.post<CalendarDatesBulkResponse>(
    getCalendarDatesBulkUrl(),
    {
      items: items.map(({ key, ...params }) => ({
        currency: params.currency ?? null,
        dateFrom: params.date_from ?? null,
        dateTo: params.date_to ?? null,
        destinationId: getCalendarDatesV2Id(params.destinationId, params.destination_id),
        hotelId: getCalendarDatesV2Id(params.hotelId, params.hotel_id),
        key,
        roomTypeId: getCalendarDatesV2Id(params.roomTypeId, params.room_type_id),
        visitTypeId: getCalendarDatesV2Id(params.visitTypeId, params.visit_type_id),
      })),
    },
    {
      headers: {
        [API_V2_PUBLIC_KEY_HEADER]: getCalendarDatesBulkPublicKey(),
        Authorization: undefined,
      },
    }
  )

export const getDatesApiRequest = (params: IDatesParamsQuery): Promise<AxiosResponse<IDate[]>> => {
  if (calendarDatesDebounceTimer) {
    clearTimeout(calendarDatesDebounceTimer)
  }

  return new Promise((resolve, reject) => {
    const requestKey = `calendar-dates-${calendarDatesRequestSequence++}`

    pendingCalendarDatesRequests.push({
      params,
      reject,
      requestKey,
      resolve,
    })

    calendarDatesDebounceTimer = setTimeout(() => {
      const queuedRequests = [...pendingCalendarDatesRequests]
      pendingCalendarDatesRequests = []
      calendarDatesDebounceTimer = null

      const deduplicatedRequests = new Map<
        string,
        {
          params: IDatesParamsQuery
          requestKeys: string[]
        }
      >()

      queuedRequests.forEach((queuedRequest) => {
        const dedupeKey = buildCalendarDatesRequestKey(queuedRequest.params)
        const existingRequest = deduplicatedRequests.get(dedupeKey)

        if (existingRequest) {
          existingRequest.requestKeys.push(queuedRequest.requestKey)
          return
        }

        deduplicatedRequests.set(dedupeKey, {
          params: queuedRequest.params,
          requestKeys: [queuedRequest.requestKey],
        })
      })

      const bulkItems = Array.from(deduplicatedRequests.values()).map((entry, index) => ({
        key: `bulk-item-${index}`,
        ...entry.params,
      }))

      const bulkRequestKeyByOriginalKey = new Map<string, string>()

      Array.from(deduplicatedRequests.values()).forEach((entry, index) => {
        const bulkItemKey = `bulk-item-${index}`
        entry.requestKeys.forEach((requestKey) => {
          bulkRequestKeyByOriginalKey.set(requestKey, bulkItemKey)
        })
      })

      getDatesBulkApiRequest(bulkItems)
        .then((response) => {
          const resultsByKey = new Map(
            response.data.results.map((result) => [result.key, result.data] as const)
          )

          queuedRequests.forEach((queuedRequest) => {
            const bulkKey = bulkRequestKeyByOriginalKey.get(queuedRequest.requestKey)
            const resultData = bulkKey ? resultsByKey.get(bulkKey) : undefined

            queuedRequest.resolve({
              ...response,
              data: resultData ?? [],
            })
          })
        })
        .catch((error) => {
          queuedRequests.forEach((queuedRequest) => {
            queuedRequest.reject(error)
          })
        })
    }, GET_ANOTHER_REQUEST_DEBOUNCE_INTERVAL)
  })
}

export const getDatesCheckoutApiRequest = (
  params: IDatesCheckoutParamsQuery
): Promise<AxiosResponse<IDateCheckout[]>> =>
  axios.get<IDateCheckout[]>('/calendar/checkouts', { params })
export const getHotelApiRequest = (
  hotelId: Identificator,
  params?: IHotelDetailParamsQuery
): Promise<AxiosResponse<IHotel>> => {
  const config: { params?: IHotelDetailParamsQuery } = {}

  if (params && Object.keys(params).length > 0) {
    config.params = params
  }

  return axios.get<IHotel>(`/hotels/${hotelId}`, config)
}
export const getHotelsApiRequest = (
  params: IHotelsParamsQuery
): Promise<AxiosResponse<IHotel[]>> => {
  if (hotelsDebounceTimer) {
    clearTimeout(hotelsDebounceTimer)
  }

  if (hotelsAbortController) {
    hotelsAbortController.abort()
  }

  return new Promise((resolve, reject) => {
    hotelsDebounceTimer = setTimeout(() => {
      hotelsAbortController = new AbortController()

      axios
        .get<IHotel[]>('/hotels', {
          params: {
            ...params,
            include_room_types: params?.include_room_types ?? true,
          },
          signal: hotelsAbortController.signal,
        })
        .then((response) => {
          hotelsAbortController = null
          resolve(response)
        })
        .catch((error) => {
          // Don't reject canceled requests - they are expected behavior
          if (!axiosLib.isCancel(error)) {
            reject(error)
          }
        })
    }, GET_ANOTHER_REQUEST_DEBOUNCE_INTERVAL)
  })
}

export const getLowestPrice = (params: URLSearchParams): Promise<AxiosResponse<IPrice>> =>
  axios.get<IPrice>('/lowest-price', { params })
export const getLoyaltyProgram = (): Promise<AxiosResponse<ILoyaltyProgram[]>> =>
  axios.get<ILoyaltyProgram[]>('/loyalty-programs')
export const getPriceApiRequest = (params: URLSearchParams): Promise<AxiosResponse<IPrice>> =>
  axios.get<IPrice>('/price', { params })
export const getVisitCategoriesApiRequest = (
  params: IVisitTypeCategoryParamsQuery
): Promise<AxiosResponse<IVisitCategory[]>> => {
  return new Promise((resolve, reject) => {
    if (visitTypeCategoriesDebounceTimer) {
      clearTimeout(visitTypeCategoriesDebounceTimer)
    }

    if (visitTypeCategoriesAbortController) {
      visitTypeCategoriesAbortController.abort()
    }

    visitTypeCategoriesDebounceTimer = setTimeout(() => {
      visitTypeCategoriesAbortController = new AbortController()

      axios
        .get<IVisitCategory[]>('/visit-type-categories', {
          params: {
            ...params,
            include_visit_types: true,
          },
          signal: visitTypeCategoriesAbortController.signal,
        })
        .then((response) => {
          visitTypeCategoriesAbortController = null
          resolve(response)
        })
        .catch((error) => {
          // Don't reject canceled requests - they are expected behavior
          if (!axiosLib.isCancel(error)) {
            reject(error)
          }
        })
    }, GET_ANOTHER_REQUEST_DEBOUNCE_INTERVAL)
  })
}

export const getDestinations = (): Promise<AxiosResponse<IDestination[]>> =>
  axios.get<IDestination[]>('/destinations')
export const sendBookingApiRequest = (
  data: IBookingRequest,
  language: ILanguage
): Promise<AxiosResponse<IBookingResponse>> =>
  axios.post('/booking', data, { headers: { 'Accept-Language': getKeyLang(language) } })
export const getBookingConfiguration = (
  params: IInitDataParametersQuery
): Promise<AxiosResponse<IInitDataResponse>> => axios.get<IInitDataResponse>('/booking', { params })
